import { useContext, useEffect, useState } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  Factory, Target, Eye, Award, Users, Recycle, 
  TrendingUp, Globe, Shield, Heart, Lightbulb, Clock,
  Loader2
} from 'lucide-react';

// Icon mapping for core values - randomly assign based on index
const valueIcons = [Shield, Recycle, Lightbulb, Heart, TrendingUp, Award, Globe, Users];
const valueColors = [
  { bg: 'bg-blue-100', text: 'text-blue-600', hover: 'hover:bg-blue-50' },
  { bg: 'bg-green-100', text: 'text-green-600', hover: 'hover:bg-green-50' },
  { bg: 'bg-purple-100', text: 'text-purple-600', hover: 'hover:bg-purple-50' },
  { bg: 'bg-red-100', text: 'text-red-500', hover: 'hover:bg-red-50' },
  { bg: 'bg-amber-100', text: 'text-amber-600', hover: 'hover:bg-amber-50' },
  { bg: 'bg-indigo-100', text: 'text-indigo-600', hover: 'hover:bg-indigo-50' },
  { bg: 'bg-teal-100', text: 'text-teal-600', hover: 'hover:bg-teal-50' },
  { bg: 'bg-pink-100', text: 'text-pink-600', hover: 'hover:bg-pink-50' },
];

// Default content when no profile data exists
const defaultContent = {
  companyName: "Swift Strips India",
  tagline: "Aluminum Recycling & Processing",
  about: "Swift Strips India is a leading aluminium recycling company dedicated to transforming aluminium scrap into high-quality products. We specialize in importing and processing various grades of aluminium scrap to produce premium aluminium strips, sheets, and alloys for diverse industrial applications.",
  ourJourney: "Founded with a vision to revolutionize the aluminium recycling industry in India, Swift Strips India has grown from a small processing unit to a state-of-the-art manufacturing facility. Our commitment to quality and sustainability has made us a trusted name in the industry.",
  ourVision: "To be the most trusted and innovative aluminium recycling company in India, setting industry benchmarks in quality, sustainability, and customer satisfaction. We envision a future where recycled aluminium meets the highest standards of purity and performance.",
  ourMission: "To deliver superior quality aluminium products through efficient recycling processes, while maintaining the highest standards of environmental responsibility. We are committed to empowering our employees, satisfying our customers, and contributing to a sustainable future.",
  coreValues: [
    { title: "Integrity", description: "Honest & Transparent" },
    { title: "Sustainability", description: "Eco-Friendly" },
    { title: "Innovation", description: "Continuous Improvement" },
    { title: "People First", description: "Employee Welfare" }
  ]
};

function Welcome() {
  const { user, API } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(defaultContent);
  
  // Get first name from full name
  const firstName = user?.name?.split(' ')[0] || 'User';

  useEffect(() => {
    const fetchLandingContent = async () => {
      try {
        const response = await axios.get(`${API}/settings/company-profile/landing-content`);
        if (response.data) {
          // Merge fetched data with defaults (use fetched if available, else default)
          setContent({
            companyName: response.data.companyName || defaultContent.companyName,
            tagline: response.data.tagline || defaultContent.tagline,
            about: response.data.about || defaultContent.about,
            ourJourney: response.data.ourJourney || defaultContent.ourJourney,
            ourVision: response.data.ourVision || defaultContent.ourVision,
            ourMission: response.data.ourMission || defaultContent.ourMission,
            coreValues: (response.data.coreValues && response.data.coreValues.length > 0) 
              ? response.data.coreValues 
              : defaultContent.coreValues
          });
        }
      } catch (error) {
        console.error('Error fetching landing content:', error);
        // Keep defaults on error
      } finally {
        setLoading(false);
      }
    };

    fetchLandingContent();
  }, [API]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="welcome-loading">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="welcome-page">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-[#1a237e] to-[#283593] rounded-2xl p-6 sm:p-8 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-white/70 text-sm">Welcome back,</p>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1">
              {firstName}!
            </h1>
            <p className="text-white/80 mt-2 text-sm sm:text-base">
              Thank you for being part of the {content.companyName} family
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <img 
              src="/logo.jpg" 
              alt={content.companyName} 
              className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl shadow-lg bg-white p-1"
            />
          </div>
        </div>
      </div>

      {/* Company Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* About Us */}
        <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2.5 bg-blue-100 rounded-lg">
              <Factory className="text-blue-600" size={22} />
            </div>
            <h2 className="text-lg font-bold text-gray-900">About {content.companyName}</h2>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            {content.about}
          </p>
          <div className="mt-4 flex items-center space-x-4 text-sm">
            <div className="flex items-center text-gray-500">
              <Globe size={16} className="mr-1.5 text-blue-500" />
              <span>Global Imports</span>
            </div>
            <div className="flex items-center text-gray-500">
              <Recycle size={16} className="mr-1.5 text-green-500" />
              <span>Sustainable</span>
            </div>
          </div>
        </div>

        {/* Our Journey */}
        <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2.5 bg-amber-100 rounded-lg">
              <Clock className="text-amber-600" size={22} />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Our Journey</h2>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            {content.ourJourney}
          </p>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-[#1a237e]">10+</p>
              <p className="text-xs text-gray-500">Years</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-[#e65100]">500+</p>
              <p className="text-xs text-gray-500">Clients</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-green-600">50K+</p>
              <p className="text-xs text-gray-500">MT/Year</p>
            </div>
          </div>
        </div>
      </div>

      {/* Vision & Mission */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Vision */}
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-5 sm:p-6 border border-purple-100">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2.5 bg-purple-100 rounded-lg">
              <Eye className="text-purple-600" size={22} />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Our Vision</h2>
          </div>
          <p className="text-gray-700 text-sm leading-relaxed">
            {content.ourVision}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              Industry Leader
            </span>
            <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
              Innovation
            </span>
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              Excellence
            </span>
          </div>
        </div>

        {/* Mission */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 sm:p-6 border border-orange-100">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2.5 bg-orange-100 rounded-lg">
              <Target className="text-orange-600" size={22} />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Our Mission</h2>
          </div>
          <p className="text-gray-700 text-sm leading-relaxed">
            {content.ourMission}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
              Quality First
            </span>
            <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
              Sustainability
            </span>
            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
              Growth
            </span>
          </div>
        </div>
      </div>

      {/* Core Values - Dynamic */}
      <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-100">
        <div className="flex items-center space-x-3 mb-5">
          <div className="p-2.5 bg-green-100 rounded-lg">
            <Award className="text-green-600" size={22} />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Our Core Values</h2>
        </div>
        <div className={`grid gap-4 ${
          content.coreValues.length <= 4 
            ? 'grid-cols-2 sm:grid-cols-4' 
            : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
        }`}>
          {content.coreValues.map((value, index) => {
            const IconComponent = valueIcons[index % valueIcons.length];
            const colors = valueColors[index % valueColors.length];
            return (
              <div 
                key={index} 
                className={`text-center p-4 bg-gray-50 rounded-xl ${colors.hover} transition-colors`}
              >
                <div className={`w-12 h-12 ${colors.bg} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <IconComponent className={colors.text} size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm">{value.title}</h3>
                <p className="text-xs text-gray-500 mt-1">{value.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 sm:p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
        <p className="text-sm text-gray-600 mb-4">
          Access your assigned modules from the navigation menu above. 
          If you need access to additional modules, please contact your administrator.
        </p>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Users size={16} />
          <span>Role: <strong className="text-gray-700">{user?.role === 'DataEntry' ? 'Data Entry' : user?.role}</strong></span>
        </div>
      </div>
    </div>
  );
}

export default Welcome;
